/**
 * Cifras de cada biblioteca (colección) a partir de `GET /api/coverage` (D-UI-7) y utilidades de las pantallas
 * Bibliotecas y Biblioteca (rediseño AI Studio, 15-10). Nada de cifras escritas a mano: todo sale de `coverage_stats`.
 */
import type { CoverageRow } from '../services/coverage.service';
import type { SearchHistoryEntry } from '../services/searchHistory.service';
import { COLLECTIONS, Collection, CollectionKey, collectionForDocument, getCollection } from './institutions';
import { extractMeaningfulTitle, parseAiSummary } from './formatters';
import type { Document } from './supabase';

export interface TipoTotal {
  tipo: string;
  total: number;
}

export interface CifrasColeccion {
  total: number;
  porTipo: TipoTotal[];
  /** Años con documentos, de menor a mayor. */
  years: number[];
  yearMin: number | null;
  yearMax: number | null;
  ultimaPublicacion: string | null;
  ultimaCarga: string | null;
}

const VACIA: CifrasColeccion = { total: 0, porTipo: [], years: [], yearMin: null, yearMax: null, ultimaPublicacion: null, ultimaCarga: null };

const maxFecha = (a: string | null, b: string | null) => (!a ? b : !b ? a : a > b ? a : b);

/** Suma las filas de la institución de la colección cuyos tipos le pertenecen (adm y conv comparten SII). */
export function cifrasColeccion(rows: CoverageRow[] | null | undefined, collection: Collection): CifrasColeccion {
  const propias = (rows || []).filter(
    r => (r.institution_code || '').toUpperCase() === collection.institution && collection.tipos.includes((r.tipo || '').toLowerCase())
  );
  if (propias.length === 0) return VACIA;
  const years = new Set<number>();
  let ultimaPublicacion: string | null = null;
  let ultimaCarga: string | null = null;
  const porTipo = collection.tipos
    .map(tipo => ({ tipo, total: propias.filter(r => r.tipo.toLowerCase() === tipo).reduce((s, r) => s + (r.total || 0), 0) }))
    .filter(t => t.total > 0);
  for (const r of propias) {
    (r.years || []).forEach(y => years.add(y));
    ultimaPublicacion = maxFecha(ultimaPublicacion, r.last_publication);
    ultimaCarga = maxFecha(ultimaCarga, r.last_ingest_at);
  }
  const sorted = Array.from(years).sort((a, b) => a - b);
  return {
    total: porTipo.reduce((s, t) => s + t.total, 0),
    porTipo,
    years: sorted,
    yearMin: sorted.length ? sorted[0] : null,
    yearMax: sorted.length ? sorted[sorted.length - 1] : null,
    ultimaPublicacion,
    ultimaCarga,
  };
}

/** «2013 – 2026», «2026» o '' si no hay años. */
export function rangoAnios(c: CifrasColeccion): string {
  if (c.yearMin == null || c.yearMax == null) return '';
  return c.yearMin === c.yearMax ? String(c.yearMin) : `${c.yearMin} – ${c.yearMax}`;
}

/** Número con separador de miles chileno (7.074). */
export const miles = (n: number) => n.toLocaleString('es-CL');

/** Etiqueta en plural de cada tipo (minúscula, como la API). */
export const TIPO_PLURAL: Record<string, string> = {
  oficio: 'Oficios',
  circular: 'Circulares',
  resolucion: 'Resoluciones',
  jurisprudencia: 'Sentencias',
  ley: 'Leyes',
  dl: 'Decretos ley',
  dfl: 'DFL',
  decreto: 'Decretos',
  convenio: 'Convenios',
};

/** Etiqueta en singular de cada tipo (segmentos del filtro y filas). */
export const TIPO_SINGULAR: Record<string, string> = {
  oficio: 'Oficio',
  circular: 'Circular',
  resolucion: 'Resolución',
  jurisprudencia: 'Sentencia',
  ley: 'Ley',
  dl: 'DL',
  dfl: 'DFL',
  decreto: 'Decreto',
  convenio: 'Convenio',
};

export const tipoSingular = (tipo: string | null | undefined) => {
  const t = (tipo || '').toLowerCase();
  return TIPO_SINGULAR[t === 'resolucion exenta' ? 'resolucion' : t] || tipo || '';
};

/**
 * Colección de una búsqueda guardada. Las búsquedas nuevas guardan `filters.coleccion`; las antiguas se deducen de
 * la institución y del tipo (sin tipo, el SII cae en Jurisprudencia administrativa).
 */
export function coleccionDeBusqueda(entry: SearchHistoryEntry): Collection | undefined {
  const f = (entry.filters || {}) as { coleccion?: string; tipo?: string };
  return getCollection(f.coleccion) || collectionForDocument(entry.institution_code, f.tipo || null);
}

/** true si la búsqueda guardada pertenece a `collection` (una del SII sin tipo ni colección vale en adm y en conv). */
export function busquedaEnColeccion(entry: SearchHistoryEntry, collection: Collection): boolean {
  const f = (entry.filters || {}) as { coleccion?: string; tipo?: string };
  if (f.coleccion) return f.coleccion === collection.key;
  if ((entry.institution_code || '').toUpperCase() !== collection.institution) return false;
  return !f.tipo || collection.tipos.includes(f.tipo.toLowerCase());
}

/**
 * Título que se muestra de un documento: en circulares y resoluciones `titulo` suele ser la oficina que las emite
 * («Subdirección Jurídica»), así que se usa la materia u objeto del resumen estructurado cuando existe.
 */
export const tituloDocumento = (doc: Pick<Document, 'ai_summary' | 'resumen' | 'tipo' | 'titulo'>) =>
  extractMeaningfulTitle(parseAiSummary(doc.ai_summary, doc.resumen), doc.tipo, doc.titulo || '');

/** Número del documento, o '' si no tiene uno real (los convenios vienen con `numero = '0'` en la BD). */
export const numeroReal = (numero: string | null | undefined) => {
  const n = (numero || '').trim();
  return /^0*$/.test(n) ? '' : n;
};

export const CLAVES: CollectionKey[] = Object.keys(COLLECTIONS) as CollectionKey[];
